import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from './context/ToastContext';
import { Link, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';

import Spinner from './Spinner';
import getArticleTitle from '../helpers/getArticleTitle';
import getArticleDescription from '../helpers/getArticleDescription';
import constants from '../constants.json' assert { type: 'json' };

import '../styles/Blog.css';
import useDynamicPath from '../hooks/useDynamicPath';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import PageNav from './PageNav';
import { GoPersonFill } from 'react-icons/go';
import { FaCalendarAlt } from 'react-icons/fa';
import { DateTime } from 'luxon';


function Blog() {
    const [articles, setArticles] = useState([]);
    const [page, setPage] = useState(0);
    const { sendMessage } = useContext(ToastContext);
    const [loading, setLoading] = useState(true);
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const { tagValue } = useParams();

    const fetchArticles = useCallback(async () => {
        try {
            const url = new URL('/api/articles/', process.env.APP_URL);
            url.searchParams.set('page', page);
            url.searchParams.set('sortBy', 'CREATED');
            url.searchParams.set('order', 'desc');
            if (tagValue) {
                url.searchParams.set('tagValue', tagValue);
            }
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
            const { articles, error, pageInfo } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }

            if (articles) {
                setArticles(articles);
            }
            if (pageInfo) {
                setPagesData({
                    limit: pageInfo.limitPerPage,
                    pages: pageInfo.totalPages,
                    count: pageInfo.totalObjects,
                });
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [page, tagValue]);

    useEffect(() => {
        fetchArticles();
    }, [fetchArticles]);

    const {
        curPage,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: 0,
        onUpdate: (newPage) => setPage(newPage),
    });

    return (
        <Container
            className={'section'}>
            <div>
                <div>
                    <div
                        style={{ marginBottom: '3rem' }} />
                    {loading ? (
                        <Spinner />
                    ): (
                        <>
                            <div
                                className={'blog--content'}
                            >
                                {articles.length ? (
                                    articles
                                        .map(({ id, title, handle, seoDescription, bodyHtml, Images, author, publishedAt, createdAt }) => {
                                            return (
                                                <div
                                                    className={'blog--item-wrapper col-12 col-lg-6 mb-5'}
                                                    key={id}>
                                                    <div
                                                        className={'blog--item-inner'}>
                                                        {Images?.at(0)?.Attachment?.url ? (
                                                            <Link
                                                                to={`${articlePath[0].pathBeforeParams}/${handle}`}
                                                                className={'blog--item-thumbnail'}>
                                                                <img
                                                                    alt={Images[0].altText || ''}
                                                                    src={Images[0].Attachment.url} />
                                                            </Link>
                                                        ) : null}
                                                        <div
                                                            className={'blog--item'}
                                                        >
                                                            <h3
                                                                className={'blog--item-title'}>
                                                                <Link
                                                                    className={'blog--item-title'}
                                                                    to={`${articlePath[0].pathBeforeParams}/${handle}`}>
                                                                    {getArticleTitle(title)}
                                                                </Link>
                                                            </h3>
                                                            <div
                                                                className={'blog--item-info'}>
                                                                <p
                                                                    className={'blog--item-info-author'}>
                                                                    {author && <>
                                                                        <GoPersonFill
                                                                            style={{ marginRight: '5px' }}
                                                                            color={'black'} />
                                                                        {author}
                                                                    </>}
                                                                    <FaCalendarAlt
                                                                        style={{
                                                                            marginRight: '5px',
                                                                            marginLeft: '10px',
                                                                        }}
                                                                        color={'black'} />
                                                                    {DateTime.fromISO(publishedAt ?? createdAt).toLocaleString(DateTime.DATE_MED)}
                                                                </p>
                                                                <p
                                                                    className={'blog--description'}
                                                                >
                                                                    {seoDescription || getArticleDescription(bodyHtml) || 'No description'}
                                                                </p>
                                                            </div>
                                                            <Link
                                                                className={'blog--button'}
                                                                to={`${articlePath[0].pathBeforeParams}/${handle}`}
                                                            >
                                                            Read more
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                ): 'No articles found.'}
                            </div>
                            <PageNav
                                curPage={curPage}
                                totalPages={pagesData.pages}
                                generatedPagesData={generatedPagesData}
                                paginateUser={paginateUser}
                            />
                        </>
                    )}
                </div>
            </div>
        </Container>
    );
}

export default Blog;